import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiSearch, FiChevronDown, FiDownload, FiChevronRight, FiBell } from 'react-icons/fi';
import { learners } from '../data/learners';
import { getRiskLevel } from '../data/helpers';
import PageHeader from '../components/ui/PageHeader';
import StatCard from '../components/ui/StatCard';
import Pill from '../components/ui/Pill';
import MiniBar from '../components/ui/MiniBar';
import ActionButton from '../components/ui/ActionButton';
import { dataFetch } from '../api';

const departments = ['All', ...new Set(learners.map(l => l.department))];

const Employees = () => {
  const [search, setSearch] = useState('');
  const [dept, setDept] = useState('All');
  const [reminders, setReminders] = useState([]);

  useEffect(() => {
    const fetchReminders = async () => {
      try {
        const res = await dataFetch('read/campaign_reminder_sent');
        if (res?.status === 'success') {
          setReminders(res.data || []);
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchReminders();
  }, []);

  const filtered = learners.filter(l => {
    const name = `${l.firstName} ${l.lastName}`.toLowerCase();
    return (name.includes(search.toLowerCase()) || l.email.toLowerCase().includes(search.toLowerCase())) &&
      (dept === 'All' || l.department === dept);
  });

  const numCompliant = learners.filter(l => l.trainingStatus === 'Compliant').length;
  const numHighRisk = learners.filter(l => l.phishingRiskScore >= 50).length;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6 max-w-[1400px] mx-auto">
      <PageHeader
        eyebrow="People"
        title="Employees"
        description="View and manage all employees across your organization."
        action={<ActionButton icon={FiDownload} label="Export" variant="outline" />}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total" value={learners.length} />
        <StatCard label="Compliant" value={numCompliant} valueColor="text-emerald-600" />
        <StatCard label="Overdue" value={learners.filter(l => l.trainingStatus === 'Overdue').length} valueColor="text-amber-600" />
        <StatCard label="High Risk" value={numHighRisk} valueColor="text-red-600" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[280px]">
          <SafeIcon icon={FiSearch} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email…"
            className="w-full pl-11 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-400 shadow-sm" />
        </div>
        <div className="relative">
          <select value={dept} onChange={e => setDept(e.target.value)} className="appearance-none px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-slate-400 shadow-sm cursor-pointer pr-10">
            {departments.map(d => <option key={d}>{d}</option>)}
          </select>
          <SafeIcon icon={FiChevronDown} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        </div>
      </div>

      <p className="text-sm text-slate-500">Showing <span className="font-semibold text-slate-700">{filtered.length}</span> of {learners.length} employees</p>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-400 font-bold bg-white">
                <th className="px-6 py-4">Employee</th>
                <th className="px-6 py-4">Department / Manager</th>
                <th className="px-6 py-4">Training Status</th>
                <th className="px-6 py-4">Risk Score</th>
                <th className="px-6 py-4">Last Phishing</th>
                <th className="px-6 py-4">Sent Frequency</th>
                <th className="px-6 py-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {filtered.map(l => {
                const risk = getRiskLevel(l.phishingRiskScore);
                const riskColor = l.phishingRiskScore < 20 ? 'bg-emerald-500' : l.phishingRiskScore < 50 ? 'bg-amber-500' : 'bg-red-500';
                const resultColor = l.lastPhishingResult === 'Reported' ? 'text-emerald-600' : l.lastPhishingResult === 'Clicked' ? 'text-amber-600' : l.lastPhishingResult === 'Submitted' ? 'text-red-600' : 'text-slate-400';
                
                const myReminders = reminders.filter(r => String(r.employee_id) === String(l.id));
                const sentFrequency = myReminders.reduce((sum, r) => sum + (r.sent_frequency || 0), 0);
                
                return (
                  <tr key={l.id} className="hover:bg-slate-50 transition-colors group bg-white">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-100 border border-slate-200 text-slate-600 font-semibold text-sm flex items-center justify-center flex-shrink-0">
                          {l.firstName[0]}{l.lastName[0]}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">{l.firstName} {l.lastName}</p>
                          <p className="text-xs text-slate-500 mt-0.5">{l.email}</p>
                          <p className="text-[11px] text-slate-400 mt-0.5">{l.jobTitle}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-semibold text-slate-900">{l.department}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{l.manager}</p>
                      <p className="text-[11px] text-slate-400 mt-0.5">{l.location}</p>
                    </td>
                    <td className="px-6 py-4"><Pill status={l.trainingStatus} /></td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1.5">
                        <Pill status={risk.label === 'Low' ? 'Low Risk' : risk.label === 'Medium' ? 'Medium' : 'High Risk'} />
                        <div className="flex items-center gap-2">
                          <MiniBar percent={l.phishingRiskScore} colorClass={riskColor} />
                          <span className="text-[10px] font-semibold text-slate-500">{l.phishingRiskScore}/100</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-xs font-semibold ${resultColor}`}>{l.lastPhishingResult}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5 text-slate-500">
                        <SafeIcon icon={FiBell} className="text-xs text-slate-400" />
                        <span className="text-xs font-semibold">{sentFrequency}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <SafeIcon icon={FiChevronRight} className="text-slate-300 group-hover:text-slate-600 transition-colors" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-3 border-t border-slate-100 text-xs text-slate-400 font-medium">
          {filtered.length} of {learners.length} employees
        </div>
      </div>
    </motion.div>
  );
};

export default Employees;